import { unwrap, type RoleOut } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Select, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";
import { useInvalidateAccess } from "./use-roles";

interface DeleteRoleDialogProps {
  role: RoleOut;
  roles: readonly RoleOut[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Deletes a custom role; a role in use needs a reassignment target first (guardrail). */
export function DeleteRoleDialog({ role, roles, open, onOpenChange }: DeleteRoleDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const [reassignTo, setReassignTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inUse = role.member_count > 0;
  const targets = roles.filter((r) => r.id !== role.id && r.key !== "owner");

  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.DELETE("/v1/roles/{role_id}", { params: { path: { role_id: role.id }, query: inUse && reassignTo ? { reassign_to: reassignTo } : {} } })),
    onSuccess: async () => {
      toast.success(t("roles.deleteDialog.deleted"));
      onOpenChange(false);
      await invalidate();
      await navigate(consoleRoutes.roles(summary.slug), { replace: true });
    },
    onError: (caught) => { setError(message(caught)); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t("roles.deleteDialog.title", { name: role.name })}
        description={t("roles.deleteDialog.body")}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" loading={remove.isPending} disabled={remove.isPending || (inUse && !reassignTo)} onClick={() => { remove.mutate(); }}>
              <Icon name="trash" />
              {t("roles.deleteDialog.submit")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
          {inUse ? (
            <>
              <FormAlert tone="warning" title={t("roles.deleteDialog.inUse", { count: role.member_count })} />
              <Field label={t("roles.deleteDialog.reassignTo")}>
                <Select value={reassignTo} onChange={(event) => { setReassignTo(event.target.value); }}>
                  <option value="">{t("roles.deleteDialog.chooseRole")}</option>
                  {targets.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
